<template>
  <a-layout
    id="app-layout"
    :class="{
      fs: isFullscreenRoute,
      'has-impersonation': isImpersonated,
      'hide-chat': isIframeRoute,
      'iframe-route': isIframeRoute,
    }"
  >
    <Teleport to="body" v-if="htmlHeader">
      <span style="display: none" v-html="htmlHeader" />
    </Teleport>
    <!-- Impersonation Banner -->
    <div v-if="isImpersonated" class="impersonation-banner">
      <div class="impersonation-banner-content">
        <font-awesome-icon
          :icon="['fas', 'user-secret']"
          class="banner-icon animate__animated animate__flash animate__infinite animate__slower"
        />
        <span>
          <strong>Modo Suplantación Activo:</strong> Estás operando en
          representación de
          <strong>{{
            loggedUser
              ? loggedUser.firstname + " " + loggedUser.lastname
              : "Cliente"
          }}</strong
          >.
        </span>
      </div>
      <a-button
        type="danger"
        size="small"
        class="exit-impersonation-btn"
        @click="handleExitImpersonation"
        :loading="exitingImpersonation"
      >
        <font-awesome-icon :icon="['fas', 'sign-out-alt']" /> Salir del modo
        suplantación
      </a-button>
    </div>

    <SideBar v-if="!isFullscreenRoute" />
    <FloatingMenu v-else />
    <a-layout
      :style="{
        marginLeft: sizeMargin,
        minHeight: '100vh',
        transition: 'ease 0.2s',
      }"
    >
      <ClientHeader v-if="!isFullscreenRoute" />
      <a-layout-content class="content-wrapper">
        <transition
          mode="out-in"
          enter-active-class="animate__animated animate__fadeIn animate__faster"
          leave-active-class="animate__animated animate__fadeOut animate__faster"
        >
          <router-view />
        </transition>
      </a-layout-content>

      <a-layout-footer
        v-if="!isFullscreenRoute && !isIframeRoute"
        class="client-footer"
      >
        <div class="footer-content">
          <span :style="{ marginRight: '10px', fontWeight: 600 }">{{
            labels.allRightsReserved(labels.appName)
          }}</span>
          <!-- social networks -->
          <div class="social-network">
            <a v-if="getUrl('facebook')" :href="getUrl('facebook')">
              <font-awesome-icon
                :icon="['fab', 'facebook-f']"
                :class="'anticon'"
              />
            </a>
            <a v-if="getUrl('twitter')" :href="getUrl('twitter')">
              <font-awesome-icon
                :icon="['fab', 'twitter']"
                :class="'anticon'"
              />
            </a>
            <a v-if="getUrl('linkedin')" :href="getUrl('linkedin')">
              <font-awesome-icon
                :icon="['fab', 'linkedin-in']"
                :class="'anticon'"
              />
            </a>
            <a v-if="getUrl('instagram')" :href="getUrl('instagram')">
              <font-awesome-icon
                :icon="['fab', 'instagram']"
                :class="'anticon'"
              />
            </a>
            <a v-if="getUrl('youtube')" :href="getUrl('youtube')">
              <font-awesome-icon
                :icon="['fab', 'youtube']"
                :class="'anticon'"
              />
            </a>
          </div>
        </div>
        <a-divider class="custom-separator" />
        <div class="footer-content-link">
          <router-link class="footer-link" :to="{ name: 'privacy-policies' }"
            >Políticas de privacidad</router-link
          >
          <router-link
            class="footer-link"
            :to="{ name: 'terms-and-conditions' }"
            >Términos y condiciones</router-link
          >
        </div>
      </a-layout-footer>
      <FloatingMobileMenu
        v-if="!isFullscreenRoute"
        :authenticated="authenticated"
        @toggle-menu="toggleMobileSidebar"
      />
    </a-layout>

    <Teleport v-if="winner && ajm" to="body">
      <WinnerAlertComponent
        v-if="winner && ajm"
        :winner="winner"
        coinAnimation="spin"
        @close="handleJackpotMarkAsViewed(winner)"
      />
    </Teleport>

    <!-- Reactive Ads Modal Component -->
    <a-modal
      v-if="currentAd"
      :visible="!!currentAd"
      :closable="true"
      :footer="null"
      :mask="true"
      :maskClosable="false"
      :keyboard="false"
      :centered="currentAd.position === 'center'"
      :width="
        isMobile ? '95%' : currentAd.width ? `${currentAd.width}px` : '800px'
      "
      :dialogStyle="
        currentAd.position === 'top' ? { top: '0px', margin: '0 auto' } : {}
      "
      wrapClassName="custom-ads-modal-wrapper"
      dialogClass="custom-ads-modal"
      @cancel="handleCloseAd"
    >
      <template slot="closeIcon">
        <span class="ad-modal-close-btn" @click.stop="handleCloseAd">
          <a-icon type="close" />
        </span>
      </template>

      <div class="ad-modal-content-container">
        <!-- Video Ad -->
        <video
          v-if="currentAd.type === 'video'"
          :src="currentAd.content"
          :class="['ads-media-content', currentAd.link ? 'clickable' : '']"
          :style="{
            width: isMobile
              ? '100%'
              : currentAd.width
              ? `${currentAd.width}px`
              : '800px',
            height: isMobile
              ? 'auto'
              : currentAd.height
              ? `${currentAd.height}px`
              : 'auto',
          }"
          autoplay
          controls
          loop
          muted
          @click="currentAd.link && handleAdClick(currentAd)"
        />

        <!-- Image Ad -->
        <img
          v-else-if="currentAd.type === 'image'"
          :src="currentAd.content"
          :alt="currentAd.title"
          :title="currentAd.title"
          :class="['ads-media-content', currentAd.link ? 'clickable' : '']"
          :style="{
            width: isMobile
              ? '100%'
              : currentAd.width
              ? `${currentAd.width}px`
              : '100%',
            height: isMobile
              ? 'auto'
              : currentAd.height
              ? `${currentAd.height}px`
              : 'auto',
            maxWidth: '100%',
          }"
          @click="currentAd.link && handleAdClick(currentAd)"
        />

        <!-- HTML Ad -->
        <div
          v-else-if="currentAd.type === 'html'"
          :class="['ads-html-content', currentAd.link ? 'clickable' : '']"
          v-html="currentAd.content"
          @click="currentAd.link && handleAdClick(currentAd)"
        />
      </div>
    </a-modal>
  </a-layout>
</template>

<script>
import SideBar from "../components/layout/client/SideBar.vue";
import FloatingMenu from "../components/layout/client/FloatingMenu.vue";
import FloatingMobileMenu from "../components/layout/client/FloatingMobileMenu.vue";
import ClientHeader from "../components/layout/client/ClientHeader.vue";
import WinnerAlertComponent from "../components/WinnerAlertComponent.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import labels from "@/utils/labels";
import { saveCookieEncode, cookieEncodeExists } from "@/utils/utils";
import Teleport from "vue2-teleport";
import _ from "lodash";
import { cloneDeep } from "lodash";
export default {
  components: {
    ClientHeader,
    SideBar,
    FloatingMenu,
    FloatingMobileMenu,
    Teleport,
    WinnerAlertComponent,
  },
  name: "AppLayout",
  beforeRouteEnter(_to, _from, next) {
    next(async (vm) => {
      await Promise.all([
        vm.handleGetPrivacyPoliciesTermsConditions(),
        vm.fetchHTMLHeader(),
        vm.fetchHeadersConfig(),
        vm.fetchHelperConfig(),
        vm.handleGetHomeAds(),
        vm.fetchWhatsappConfig(),
      ]);
    });
  },
  data() {
    return {
      labels: labels,
      showed: false,
      ads: [],
      currentAd: null,
      winner: null,
      notificationChecker: null,
      notifications: [],
      errorCount: 0,
      exitingImpersonation: false,
      isMobile: false,
    };
  },
  mounted() {
    this.checkIfMobile();
    window.addEventListener("resize", this.checkIfMobile);
  },
  methods: {
    checkIfMobile() {
      this.isMobile = window.innerWidth <= 600;
    },
    toggleMobileSidebar() {
      this.$store.dispatch("layout/updateLayout", {
        ...this.layout,
        collapsed: !this.layout.collapsed,
      });
    },
    ...mapActions("ads", [
      "getHomeAds",
      "getUserAds",
      "markAsViewed",
      "markAsClicked",
    ]),
    ...mapActions("jackpot", ["getNotifications", "markViewed"]),
    ...mapActions("clientAuth", ["checkSession", "handleImpersonationExit"]),
    ...mapActions("systemConfig", [
      "fetchSocialNetworks",
      "getPrivacyPoliciesTermsConditions",
      "fetchHTMLHeader",
      "fetchHeadersConfig",
      "fetchHelperConfig",
      "fetchWhatsappConfig",
    ]),
    showReferralNotification() {
      this.$nextTick(() => {
        this.$notification.warning({
          message: this.labels.home.referralMessage,
          duration: 15,
          closable: true,
          class: "referral-notification",
        });
      });
    },
    async getSocialNetworksConfig() {
      try {
        await this.fetchSocialNetworks();
      } catch (error) {
        console.error(error);
      }
    },
    async handleGetPrivacyPoliciesTermsConditions() {
      try {
        await this.getPrivacyPoliciesTermsConditions();
      } catch (error) {
        console.error(error);
      }
    },
    getUrl(name) {
      const { socialNetworks = [] } = this;
      const socialNetwork = socialNetworks.find(
        (item) => !!item?.label?.toLowerCase()?.includes(name)
      );
      if (!socialNetwork) {
        return null;
      }
      return socialNetwork.value;
    },
    showAds(ads) {
      if (!ads || ads.length === 0 || this.currentAd) {
        return;
      }
      const ad = _.cloneDeep(ads)[0];
      if (!ad) return;

      this.currentAd = ad;
      this.handleMarkAsViewed(ad);
    },
    handleCloseAd() {
      if (this.currentAd) {
        const closedId = this.currentAd.id;
        this.currentAd = null;
        const remaining = this.ads.filter((ad) => ad.id !== closedId);
        this.ads = remaining;
        if (remaining.length > 0) {
          this.$nextTick(() => {
            this.showAds(remaining);
          });
        }
      }
    },
    handleAdClick(ad) {
      if (ad?.link) {
        this.handleMarkAsClicked(ad);
        window.open(ad.link, "_blank");
      }
    },
    async handleMarkAsViewed(ad) {
      try {
        if (this.authenticated) {
          await this.markAsViewed(ad.id);
        }
        if (!this.authenticated || !ad?.auth) {
          saveCookieEncode(`ads-${ad.id}-viewed`, "1", 24);
        }
      } catch (error) {
        console.error(error);
      }
    },
    async handleMarkAsClicked(ad) {
      try {
        if (this.authenticated) {
          await this.markAsClicked(ad.id);
        }
      } catch (error) {
        console.error(error);
      }
    },
    async handleGetHomeAds() {
      try {
        const response = await this.getHomeAds();
        if (response) {
          this.ads = _.uniqBy(
            [...this.ads, ...(response?.data?.data || [])],
            "id"
          )
            .filter((ad) => !cookieEncodeExists(`ads-${ad.id}-viewed`, "1"))
            .map((ad) => ({
              ...ad,
              ...("auth" in ad ? {} : { auth: false }),
            }));
        }
      } catch (error) {
        console.error(error);
      }
    },
    async handleGetUserAds() {
      try {
        const response = await this.getUserAds();
        if (response) {
          this.ads = _.uniqBy(
            [...this.ads, ...(response?.data?.data || [])],
            "id"
          ).map((ad) => ({
            ...ad,
            ...("auth" in ad ? {} : { auth: true }),
          }));
        }
      } catch (error) {
        console.error(error);
      }
    },
    async checkNotifications() {
      try {
        const response = await this.getNotifications();
        if (response?.data?.length > 0) {
          this.notifications = response.data;
        }
      } catch (error) {
        this.errorCount++;
        if (this.errorCount >= 3) {
          this.stopNotificationChecker();
          this.errorCount = 0;
        }
      }
    },
    async startNotificationChecker() {
      if (this.notificationChecker) {
        clearInterval(this.notificationChecker);
      }
      this.notificationChecker = setInterval(async () => {
        this.checkNotifications();
      }, 180000); // 3 minutes
    },
    async stopNotificationChecker() {
      if (this.notificationChecker) {
        clearInterval(this.notificationChecker);
        this.notificationChecker = null;
      }
    },
    startShowingNotifications(notifications) {
      if (!notifications || notifications.length === 0) {
        return;
      }
      const firstNotification = cloneDeep(notifications).shift();
      this.winner = {
        ...firstNotification,
        name: `${this.loggedUser?.first_name || ""} ${
          this.loggedUser?.last_name || ""
        }`,
        currency: "RD$",
      };
    },
    async handleJackpotMarkAsViewed(winner) {
      try {
        if (this.authenticated && winner?.id) {
          await this.markViewed(winner);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.notifications = this.notifications.filter(
          (notification) => notification.id !== winner.id
        );
        if (this.winner?.id) {
          this.$nextTick(() => {
            this.checkSession();
          });
        }
        this.winner = null;
      }
    },
    insertHeaderWTT() {
      const wttApi = window.WTT_API || {};
      wttApi.config = {
        token:
          "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjb21wYW55SWQiOiI2ZjViNjdkNC00ZjIxLTQwMGEtYjA4Yy01ODEzZjI4ZjA3ZmEiLCJjb25uZWN0aW9uSWQiOiIwOWU1MmMwZC0wMjZmLTRjYzctODJhYy0wN2YzYTFmN2M5M2QiLCJpYXQiOjE3NDA1ODczNTl9.iQrmUc1lXKWEEMJlpAhaOTwxxDSU3TiEgybV0lw7NqU",
        title: "¡Bienvenido al soporte!", // a title to be presented on the widget
        subtitle: "¿Cómo podemos ayudar?", // (optional) a subtitle to be presented on the widget
        position: "left", // (optional) values: left or right / default: left
        initialMessages: ["Hola, ¿cómo podemos ayudarte?"], // (optional) values: an array of messages to be shown when the contact open the widget for the first time
        toggleButtonEnabled: true, // (optional) enables a toggle button that opens/close the widget
        toggleButtonBgColor: "#42a5f5", // (optional) the html background color of the toggle button
        isAudioEnabled: false, // enable the option to the widget user send audio messages
      };
      window.WTT_API = wttApi;
      const script = document.createElement("script");
      script.setAttribute("async", "");
      script.setAttribute("crossorigin", "*");
      script.src =
        "https://public.whaticket.com/widget/production/wtt-widget-default.js";
      script.onload = () => {
        setTimeout(() => {
          const iframe = document.getElementById("wtt-widget-iframe");
          if (iframe) {
            iframe.style.setProperty("bottom", "5rem", "important");
          }
        }, 200);
      };
      document.head.appendChild(script);
    },
    insertHeaderCallbell() {
      const callbellSettings = window.callbellSettings || {};
      callbellSettings.uuid = "9293f23c-d154-445e-9dc1-d7c715439078";
      callbellSettings.script_token = "Bt5BgUT3geXLj5ZNbLHGzwiF";
      window.callbellSettings = callbellSettings;
      const script = document.createElement("script");
      script.setAttribute("async", "");
      script.src = `https://dash.callbell.eu/include/livechat/${callbellSettings.script_token}/${callbellSettings.uuid}.js`;

      // setInterval to aggressively find the Callbell widget, even if hidden inside a Shadow DOM
      setInterval(() => {
        const isMobile = window.innerWidth <= 1199;

        const host = document.getElementById("callbell-livechat-host");
        if (host) {
          if (this.isIframeRoute) {
            host.style.setProperty("display", "none", "important");
            host.style.setProperty("visibility", "hidden", "important");
            host.style.setProperty("pointer-events", "none", "important");
            return;
          } else {
            if (host.style.getPropertyValue("display") === "none") {
              host.style.removeProperty("display");
              host.style.removeProperty("visibility");
              host.style.removeProperty("pointer-events");
            }
          }
          // It could be in a shadowRoot or directly inside the host
          const root = host.shadowRoot || host;

          // No need to inject global style since we dynamically manage inline styles

          // Also dynamically force the inline style for any fixed child
          const elements = root.querySelectorAll("*");
          elements.forEach((el) => {
            const computed = window.getComputedStyle(el);
            if (
              computed.position === "fixed" ||
              computed.position === "absolute"
            ) {
              if (isMobile) {
                const currentBottom = el.style.getPropertyValue("bottom");
                if (
                  currentBottom !== "80px" ||
                  el.style.getPropertyPriority("bottom") !== "important"
                ) {
                  el.style.setProperty("bottom", "80px", "important");
                }
              } else {
                // Remove the override on desktop to let Callbell manage its own position
                if (el.style.getPropertyValue("bottom") === "80px") {
                  el.style.removeProperty("bottom");
                }
              }
            }
          });
        }
      }, 1000);

      document.head.appendChild(script);
    },
    updateBodyScroll() {
      if (typeof document !== "undefined") {
        if (this.isIframeRoute) {
          document.documentElement.classList.add("iframe-no-scroll");
          document.body.classList.add("iframe-no-scroll");
        } else {
          document.documentElement.classList.remove("iframe-no-scroll");
          document.body.classList.remove("iframe-no-scroll");
        }
      }
    },
    updateChatVisibility() {
      const shouldHide = this.isIframeRoute;
      const host = document.getElementById("callbell-livechat-host");
      if (host) {
        if (shouldHide) {
          host.style.setProperty("display", "none", "important");
          host.style.setProperty("visibility", "hidden", "important");
          host.style.setProperty("pointer-events", "none", "important");
        } else {
          host.style.removeProperty("display");
          host.style.removeProperty("visibility");
          host.style.removeProperty("pointer-events");
        }
      }
      const wttIframe = document.getElementById("wtt-widget-iframe");
      if (wttIframe) {
        if (shouldHide) {
          wttIframe.style.setProperty("display", "none", "important");
          wttIframe.style.setProperty("visibility", "hidden", "important");
          wttIframe.style.setProperty("pointer-events", "none", "important");
        } else {
          wttIframe.style.removeProperty("display");
          wttIframe.style.removeProperty("visibility");
          wttIframe.style.removeProperty("pointer-events");
        }
      }
    },
    async handleExitImpersonation() {
      try {
        this.exitingImpersonation = true;
        await this.handleImpersonationExit();
        this.$message.success("Has salido del modo suplantación.");
        this.$router.push({ name: "login" });
      } catch (error) {
        console.error(error);
        this.$message.error("Error al salir del modo suplantación.");
      } finally {
        this.exitingImpersonation = false;
      }
    },
  },
  computed: {
    ...mapGetters("layout", ["getLayoutDataStore"]),
    ...mapState("clientAuth", ["authenticated"]),
    ...mapState("systemConfig", ["socialNetworks"]),
    ...mapGetters({
      htmlHeader: "systemConfig/getHTMLHeader",
      loggedUser: "clientAuth/getLoggedUser",
      headersConfig: "systemConfig/getHeadersConfig",
    }),
    totalAds() {
      return this.ads?.length || 0;
    },
    layout() {
      return this.getLayoutDataStore;
    },
    breakPoint() {
      return this.layout.screenWidth <= 1199;
    },
    sizeMargin() {
      if (this.isFullscreenRoute) {
        return "0px";
      }
      return this.layout.breakPoint
        ? "0px"
        : this.layout.collapsed
        ? "88px"
        : "256px";
    },
    ajm() {
      return this.loggedUser?.ajm || false;
    },
    isFullscreenRoute() {
      return [
        "roulettes",
        "live-lottery",
        "dog-racing",
        "sdominoes",
        "parchis",
        "loto-scratch",
      ].includes(this.$route?.name);
    },
    isImpersonated() {
      return this.loggedUser?.is_impersonated || false;
    },
    isIframeRoute() {
      const iframeRoutes = [
        "flash-draw",
        "flash-roulette",
        "anguilla-pool",
        "tombola-san",
        "roulettes",
        "live-lottery",
        "dog-racing",
        "loto-scratch",
        "sdominoes",
        "parchis",
      ];
      return (
        iframeRoutes.includes(this.$route?.name) ||
        this.$route?.meta?.hasIframe === true ||
        this.$route?.meta?.hideChat === true
      );
    },
  },
  watch: {
    $route: {
      handler() {
        this.$nextTick(() => {
          this.updateChatVisibility();
          this.updateBodyScroll();
        });
      },
      immediate: true,
    },
    headersConfig: {
      handler(val, old) {
        if (_.isEqual(val, old)) return;
        if (val?.ecw || val?.eww) {
          this.$nextTick(() => {
            if (val?.ecw) {
              this.insertHeaderCallbell();
            }
            if (val?.eww) {
              this.insertHeaderWTT();
            }
          });
        }
      },
      immediate: true,
      deep: true,
    },
    loggedUser: {
      async handler(val) {
        if (val && val?.ajm) {
          await Promise.all([
            this.checkNotifications(),
            this.startNotificationChecker(),
          ]);
        }
      },
      immediate: true,
      deep: true,
    },
    authenticated() {
      if (this.loggedUser && this.loggedUser?.iru && !this.showed) {
        this.showed = true;
        this.showReferralNotification();
      }
      if (this.loggedUser) {
        this.handleGetUserAds();
      }
    },
    ads: {
      handler(val, old) {
        if (!_.isEqual(val, old) && val && val.length > 0 && !this.currentAd) {
          this.$nextTick(() => {
            this.showAds(_.cloneDeep(val));
          });
        }
      },
      deep: true,
    },
    notifications: {
      handler(val = [], old) {
        if (!_.isEqual(val, old) && val.length > 0) {
          this.$nextTick(() => {
            this.startShowingNotifications(val);
          });
        }
      },
      deep: true,
    },
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkIfMobile);
    this.stopNotificationChecker();
    if (typeof document !== "undefined") {
      document.documentElement.classList.remove("iframe-no-scroll");
      document.body.classList.remove("iframe-no-scroll");
    }
  },
};
</script>

<style lang="scss">
@import "../assets/scss/variable";

.impersonation-banner {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 50px;
  background: linear-gradient(135deg, #d32f2f, #b71c1c);
  color: #ffffff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
  z-index: 99999;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.35);
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  transition: all 0.3s ease;

  .impersonation-banner-content {
    display: flex;
    align-items: center;
    gap: 10px;

    .banner-icon {
      font-size: 18px;
      color: #ffeb3b;
    }
  }

  .exit-impersonation-btn {
    background: #ffffff !important;
    border-color: #ffffff !important;
    color: #b71c1c !important;
    font-weight: 600;
    border-radius: 4px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;

    &:hover {
      background: #ffebee !important;
      color: #c62828 !important;
      transform: translateY(-1px);
    }
  }
}

#app-layout {
  flex-direction: column;

  &.has-impersonation {
    margin-top: 50px;

    .sidebar-container {
      top: 50px !important;
      height: calc(100vh - 50px) !important;
    }

    .client-header {
      top: 50px !important;
    }
  }

  .content-wrapper {
    margin-top: 74px;
    min-height: calc(100vh - 84px - 74px);
    display: flex;
    width: 100% !important;
    height: auto;
    background: var(--background);
    color: var(--text);
    padding-bottom: 60px;
    @media screen and (max-width: $screen-mobile) {
      min-height: calc(100vh - 84px - 60px);
      padding-bottom: 50px;
      margin-top: 60px;
      transition: 1s ease-in-out;
    }
  }
  .client-footer {
    background-color: var(--footer-bg);
    color: var(--text);
    font-family: "Roboto", sans-serif;
    display: flex;
    width: 100% !important;
    flex-direction: column;
    position: relative;
    height: auto;
    @media screen and (max-width: $screen-mobile) {
      padding: 24px 20px;
      transition: 0.5s ease-in-out;
    }
    .footer-content {
      display: flex !important;
      justify-content: space-between !important;
      width: 100% !important;
      align-items: center;
      transition: 0.5s ease-in-out;
      .social-network {
        display: flex !important;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        gap: 10px;
        a {
          color: color(--white);
          width: 36px !important;
          height: 36px !important;
          border: 1px solid var(--text);
          text-decoration: none;
          font-weight: 400;
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 50%;
          &:hover {
            transition: ease-in-out 0.3s;
            background-color: var(--hover);
          }
          .anticon {
            font-size: 20px !important;
          }
        }
      }
      @media screen and (max-width: 600px) {
        flex-direction: column-reverse;
        gap: 15px;
        text-align: center !important;
        transition: 0.5s ease-in-out;
      }
      &-link {
        .footer-link {
          color: var(--link);
          text-decoration: underline;
          &:not(:last-child) {
            padding-right: 5px;
            margin-right: 5px;
            border-right: 1px solid var(--text-muted);
          }
        }
      }
    }
    .custom-separator {
      background: var(--text-muted);
    }
  }
  &.fs {
    height: 100vh !important;
    max-height: 100vh !important;
    overflow: hidden !important;
    .client-footer,
    .client-header,
    .side-bar {
      display: none !important;
    }
    .content-wrapper {
      margin: 0px !important;
      margin-top: 0px !important;
      height: 100vh !important;
      height: 100dvh !important;
      min-height: 100vh !important;
      min-height: 100dvh !important;
      max-height: 100vh !important;
      max-height: 100dvh !important;
      padding: 0px !important;
      overflow: hidden !important;
      transition: 0.5s ease-in-out;
    }
    .app-layout {
      min-height: 100vh !important;
    }
  }
}
.custom-ads-modal-wrapper {
  .ant-modal {
    padding-bottom: 0;
  }
  .ant-modal-content {
    background-color: transparent !important;
    box-shadow: none !important;
    border-radius: 12px;
    overflow: visible;
    position: relative;
  }
  .ant-modal-body {
    padding: 0 !important;
    background-color: transparent;
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  }
  .ant-modal-close {
    top: 0;
    right: 0;
    width: auto;
    height: auto;
    line-height: 1;
    z-index: 1000;
  }
  .ad-modal-close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #ff4d4f;
    color: #ffffff;
    padding: 10px 14px;
    font-size: 16px;
    font-weight: bold;
    border-bottom-left-radius: 10px;
    cursor: pointer;
    transition: all 0.2s ease;
    &:hover {
      background-color: #f5222d;
      transform: scale(1.05);
    }
  }
  .ad-modal-content-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    overflow: hidden;
    border-radius: 12px;
    background-color: var(--modal-bg, #1f1f1f);
  }
  .ads-media-content {
    display: block;
    width: 100%;
    height: auto;
    object-fit: contain;
    border-radius: 12px;
    &.clickable {
      cursor: pointer !important;
    }
  }
  .ads-html-content {
    width: 100%;
    user-select: none;
    &.clickable {
      cursor: pointer !important;
    }
  }
}
.referral-notification {
  background-color: var(--notification-bg);
  .ant-notification-notice-message,
  .ant-notification-notice-description,
  .ant-notification-notice-close {
    color: var(--notification-text);
  }
}
.error-409 {
  .ant-modal-content {
    background-color: var(--modal-bg) !important;
    box-shadow: 0 1px 3px 3px var(--modal-shadow);
    .ant-modal-confirm-title {
      color: var(--text) !important;
    }
    .ant-modal-confirm-content {
      color: var(--text) !important;
      .link {
        color: var(--text-active);
        font-family: "Roboto", sans-serif;
        text-decoration: none;
        font-size: 1rem;
        font-weight: 500;
        margin-left: 5px;
        &:hover {
          text-decoration: underline;
        }
      }
      .error-list-item {
        font-size: 0.95rem;
        margin-bottom: 5px;
        &:last-child {
          margin-bottom: 0;
        }
      }
    }
    .ant-modal-confirm-btns {
      .ant-btn {
        background-color: var(--btn-bg-primary) !important;
        border-color: var(--btn-bc-primary) !important;
        color: var(--btn-text-primary) !important;
        &:hover,
        &:focus,
        &:active {
          background-color: var(--btn-bg-primary) !important;
          border-color: var(--btn-bc-primary) !important;
          color: var(--btn-text-primary) !important;
        }
        &.ant-btn-primary {
          background-color: var(--btn-bg-secondary) !important;
          border-color: var(--btn-bc-secondary) !important;
          color: var(--btn-text-secondary) !important;
          &:hover {
            background-color: var(--btn-bg-secondary) !important;
            border-color: var(--btn-bc-secondary) !important;
            color: var(--btn-text-secondary) !important;
          }
        }
      }
    }
    .ant-form-item-label > label {
      color: var(--text) !important;
    }
  }
}

/* Callbell Chat Widget Global Override */
@media screen and (max-width: 1199px) {
  #callbell-livechat-host {
    bottom: 80px !important;
  }
}

/* Disable outer scrolling when inside iframe game routes */
html.iframe-no-scroll,
body.iframe-no-scroll {
  overflow: hidden !important;
  height: 100% !important;
  max-height: 100% !important;
  /* Evita que el scroll del iframe se encadene al documento padre (iOS) */
  overscroll-behavior: none;
}

#app-layout.iframe-route:not(.fs) {
  height: 100vh !important;
  height: 100dvh !important;
  max-height: 100vh !important;
  max-height: 100dvh !important;
  overflow: hidden !important;

  /* En iOS 100vh = viewport grande; usar dvh para que el padre no sea desplazable */
  > .ant-layout {
    min-height: 100dvh !important;
    height: 100dvh !important;
    overflow: hidden !important;
  }

  .client-footer {
    display: none !important;
  }

  .content-wrapper {
    margin-top: 74px !important;
    height: calc(100vh - 74px) !important;
    min-height: calc(100vh - 74px) !important;
    max-height: calc(100vh - 74px) !important;
    padding-bottom: 0 !important;
    overflow: hidden !important;

    @media screen and (max-width: $screen-mobile) {
      margin-top: 61px !important;
      height: calc(100vh - 61px - 70px) !important;
      height: calc(100dvh - 61px - 70px) !important;
      min-height: calc(100vh - 61px - 70px) !important;
      min-height: calc(100dvh - 61px - 70px) !important;
      max-height: calc(100vh - 61px - 70px) !important;
      max-height: calc(100dvh - 61px - 70px) !important;
      padding-bottom: 0 !important;
      overflow: hidden !important;
    }
  }
}

#app-layout.fs,
#app-layout.fs.iframe-route {
  height: 100vh !important;
  max-height: 100vh !important;
  overflow: hidden !important;

  .client-footer,
  .client-header,
  .side-bar {
    display: none !important;
  }

  .content-wrapper {
    margin: 0px !important;
    margin-top: 0px !important;
    height: 100vh !important;
    height: 100dvh !important;
    min-height: 100vh !important;
    min-height: 100dvh !important;
    max-height: 100vh !important;
    max-height: 100dvh !important;
    padding: 0px !important;
    overflow: hidden !important;
    transition: 0.5s ease-in-out;
  }

  .app-layout {
    min-height: 100vh !important;
  }
}

/* Hide chat widgets in iframe/game views */
.hide-chat #callbell-livechat-host,
.hide-chat #wtt-widget-iframe,
#app-layout.hide-chat ~ #callbell-livechat-host,
#app-layout.hide-chat ~ #wtt-widget-iframe,
body:has(#app-layout.hide-chat) #callbell-livechat-host,
body:has(#app-layout.hide-chat) #wtt-widget-iframe {
  display: none !important;
  visibility: hidden !important;
  pointer-events: none !important;
  opacity: 0 !important;
}

/* Global mobile override for Tour Helper buttons */
@media screen and (max-width: 767px) {
  [class*="-tour-btn"] {
    bottom: 140px !important;
    right: 25px !important;
    z-index: 998 !important;
  }
}
</style>
